import { useThree } from '@react-three/fiber';

import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';

import { animationFrame } from 'utils/animationFrame';
import { createVideoFromBlobs } from 'utils/createVideoFromBlobs';

import useFfmpeg, { ProgressInfo } from './useFfmpeg';

export default function useCanvasRecorder(
  mixer: ReturnType<typeof useFrameMixer> | undefined,
  config: {
    width?: number;
    height?: number;
    frameRate: number;
    playbackSpeed: number;
    backgroundColor?: string;
  },
) {
  const { gl, scene, camera } = useThree();
  const { loadFfmpeg, createProgressTracker } = useFfmpeg();

  const params = new URLSearchParams(window.location.search);
  const mp4MaxFrames = params.get('mp4MaxFrames')
    ? parseInt(params.get('mp4MaxFrames')!)
    : undefined;

  const recordVideo = async (
    onProgress?: (progress: ProgressInfo) => void,
    signal?: AbortSignal,
  ) => {
    if (!mixer || !config.width || !config.height) return;

    const ffmpegPromise = loadFfmpeg();
    const startTime = Date.now();

    const recordedChunks: Blob[] = [];
    const maxFrames =
      mp4MaxFrames && mixer.duration.frames > mp4MaxFrames
        ? mp4MaxFrames
        : mixer.duration.frames;

    console.time('recording chunks');

    // Set frame to 0 and then await next frame so camera has time to update
    mixer.setFrame(0);
    await animationFrame();

    // Progress tracking for recording phase (0-20%)
    const reportRecordingProgress = (currentFrame: number) => {
      if (onProgress) {
        const recordingPercentage = (currentFrame / maxFrames) * 20; // First 20%
        const elapsedSeconds = currentFrame / config.frameRate;
        const minutes = Math.floor(elapsedSeconds / 60);
        const seconds = (elapsedSeconds % 60).toFixed(2);
        const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.padStart(5, '0')}`;

        onProgress({
          percentage: recordingPercentage,
          time: timeString,
        });
      }
    };

    for (let recFrame = 0; recFrame < maxFrames; recFrame++) {
      // Check for cancellation at the start of each frame
      if (signal?.aborted) {
        console.log('Recording cancelled at frame', recFrame);
        return undefined;
      }

      mixer.setFrame(recFrame);
      gl.render(scene, camera);
      // await next frame to make sure ui stays responsive:
      // character, camera, and shadows all need to update
      await animationFrame();
      const data = gl.domElement.toDataURL();
      const blob = dataURLtoBlob(data);
      recordedChunks.push(blob);

      // Report progress every 10 frames to avoid too many updates
      if (recFrame % 10 === 0 || recFrame === maxFrames - 1) {
        reportRecordingProgress(recFrame + 1);
      }
    }
    console.timeEnd('recording chunks');

    // Check again before the expensive FFmpeg processing
    if (signal?.aborted) {
      console.log('Recording cancelled before FFmpeg processing');
      return undefined;
    }

    const ffmpeg = await ffmpegPromise;
    console.log('ffmpeg ready');

    console.time('video from blobs');

    // Progress tracking for video creation phase (20-100%)
    const reportVideoCreationProgress = (progress: ProgressInfo) => {
      if (onProgress) {
        // Scale the FFmpeg progress from 20% to 100%
        const scaledPercentage = 20 + progress.percentage * 0.8;

        // Calculate total elapsed time including recording phase
        const totalElapsedMs = Date.now() - startTime;
        const totalElapsedSeconds = totalElapsedMs / 1000;
        const totalMinutes = Math.floor(totalElapsedSeconds / 60);
        const totalSeconds = (totalElapsedSeconds % 60).toFixed(2);
        const totalTimeString = `${totalMinutes.toString().padStart(2, '0')}:${totalSeconds.padStart(5, '0')}`;

        onProgress({
          percentage: scaledPercentage,
          time: totalTimeString,
        });
      }
    };

    const video = await createVideoFromBlobs(
      recordedChunks,
      'output.mp4',
      ffmpeg,
      config.width,
      config.height,
      config.frameRate,
      config.playbackSpeed,
      config.backgroundColor,
      reportVideoCreationProgress,
      createProgressTracker,
    );
    console.timeEnd('video from blobs');
    return video;
  };

  return {
    recordVideo,
  };
}

const dataURLtoBlob = (dataUrl: string) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};
